'use client';

import {
  Info,
  Layout,
} from 'lucide-react';
import { Orbit } from '@/components/animate-ui/icons/orbit';
import { Router } from '@/components/animate-ui/icons/router';
import { AnimateIcon } from '@/components/animate-ui/icons/icon';

import { motion, type Variants, type Transition } from 'motion/react';

const BUTTON_MOTION_CONFIG = {
  initial: 'rest',
  whileHover: 'hover',
  whileTap: 'tap',
  variants: {
    rest: { maxWidth: '44px' },
    hover: {
      maxWidth: '180px',
      transition: { type: 'spring', stiffness: 200, damping: 35, delay: 0.15 },
    },
    tap: { scale: 0.95 },
  },
  transition: { type: 'spring', stiffness: 250, damping: 25 },
} as const;

const LABEL_VARIANTS: Variants = {
  rest: { opacity: 0, x: 4 },
  hover: { opacity: 1, x: 0, visibility: 'visible' },
  tap: { opacity: 1, x: 0, visibility: 'visible' },
};

const LABEL_TRANSITION: Transition = {
  type: 'spring',
  stiffness: 200,
  damping: 25,
};

interface ManagementBarProps {
  onNavigate?: (page: "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering" | "about" | "socials", targetId?: string) => void;
}

function ManagementBar({ onNavigate }: ManagementBarProps) {
  return (
    <div className="w-full flex justify-center">
      <div className="flex w-fit flex-row items-center gap-x-2 p-1.5 scale-95 sm:scale-105">
        <div className="mx-auto flex flex-row shrink-0 items-center">
          <motion.div
            layout
            layoutRoot
            className="mx-auto flex space-x-1.5"
          >
            <AnimateIcon animateOnHover loop asChild>
              <motion.button
                {...BUTTON_MOTION_CONFIG}
                onClick={() => onNavigate?.("home", "industries")}
                className="flex h-11 items-center space-x-2.5 overflow-hidden whitespace-nowrap rounded-xl bg-white/5 px-3 text-white/60 hover:bg-white/10 transition-colors cursor-pointer"
                aria-label="Services"
              >
                <Router size={20} className="shrink-0" />
                <motion.span
                  variants={LABEL_VARIANTS}
                  transition={LABEL_TRANSITION}
                  className="invisible text-sm font-medium"
                >
                  Services Provided
                </motion.span>
              </motion.button>
            </AnimateIcon>

            <motion.button
              {...BUTTON_MOTION_CONFIG}
              onClick={() => onNavigate?.("about")}
              className="flex h-11 items-center space-x-2.5 overflow-hidden whitespace-nowrap rounded-xl bg-blue-500/10 px-3 text-blue-400 hover:bg-blue-500/20 transition-colors cursor-pointer"
              aria-label="About"
            >
              <motion.div
                variants={{
                  rest: { rotate: 0, scale: 1 },
                  hover: { 
                    rotate: 360, 
                    scale: 1.15,
                    transition: { 
                      rotate: { repeat: Infinity, duration: 3, ease: "linear" },
                      scale: { type: "spring", stiffness: 220, damping: 15 }
                    } 
                  }
                }}
                className="shrink-0 flex items-center justify-center"
              >
                <Info size={20} />
              </motion.div>
              <motion.span
                variants={LABEL_VARIANTS}
                transition={LABEL_TRANSITION}
                className="invisible text-sm font-medium"
              >
                Who Wesitex ?
              </motion.span>
            </motion.button>

            <AnimateIcon animateOnHover asChild>
              <motion.button
                {...BUTTON_MOTION_CONFIG}
                onClick={() => onNavigate?.("socials")}
                className="flex h-11 items-center space-x-2.5 overflow-hidden whitespace-nowrap rounded-xl bg-brand-purple/10 px-3 text-brand-electric hover:bg-brand-purple/20 transition-colors cursor-pointer"
                aria-label="Socials"
              >
                <Orbit size={20} className="shrink-0" />
                <motion.span
                  variants={LABEL_VARIANTS}
                  transition={LABEL_TRANSITION}
                  className="invisible text-sm font-medium"
                >
                  Social Media
                </motion.span>
              </motion.button>
            </AnimateIcon>
          </motion.div>
        </div>

        <div className="mx-3 h-8 w-px bg-brand-purple/20 rounded-full" />

        <motion.button
          whileTap={{ scale: 0.975 }}
          onClick={() => onNavigate?.("home", "products")}
          className="flex h-11 text-sm cursor-pointer items-center justify-center rounded-xl bg-brand-purple px-4 py-2.5 text-white transition-all duration-300 hover:bg-brand-purple/90 hover:shadow-[0_0_15px_rgba(139,92,246,0.5)]"
        >
          <span className="mr-1 text-white/70">View:</span>
          <span className="font-medium whitespace-nowrap">Our Software</span>
          <div className="mx-2 h-5 w-px bg-white/20 rounded-full" />
          <div className="flex items-center gap-1 rounded-md bg-white/10 px-1.5 py-0.5 -mr-1">
            <Layout size={14} />
          </div>
        </motion.button>
      </div>
    </div>
  );
}

export { ManagementBar };
